import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import SelectInput from '@components/atoms/ui-kit/select-input';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface MonthSelectorProps {
  value: string; // YYYY-MM format
  onChange: (value: string) => void;
  className?: string;
}

export default function MonthSelector({
  value,
  onChange,
  className
}: MonthSelectorProps) {
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();

  // Generate last 12 months as options
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
      options.push({ value: yearMonth, label });
    }

    return options;
  }, []);

  const currentOption = monthOptions.find(opt => opt.value === value);

  const handlePrevMonth = () => {
    const currentIndex = monthOptions.findIndex(opt => opt.value === value);
    if (currentIndex < monthOptions.length - 1) {
      onChange(monthOptions[currentIndex + 1].value);
    }
  };

  const handleNextMonth = () => {
    const currentIndex = monthOptions.findIndex(opt => opt.value === value);
    if (currentIndex > 0) {
      onChange(monthOptions[currentIndex - 1].value);
    }
  };

  const canGoPrev =
    monthOptions.findIndex(opt => opt.value === value) <
    monthOptions.length - 1;
  const canGoNext = monthOptions.findIndex(opt => opt.value === value) > 0;

  const chipBase =
    'p-1.5 rounded-full border border-hairline transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className={`flex items-center gap-2 ${className || ''}`}>
      <button
        type="button"
        onClick={handlePrevMonth}
        disabled={!canGoPrev}
        className={chipBase}
        style={{ color: getColour('normalText') }}
        onMouseEnter={e => {
          if (canGoPrev)
            e.currentTarget.style.backgroundColor = getBrandTint(0.06);
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = '';
        }}
        title={t('previous-month')}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <div className="w-48">
        <SelectInput
          options={monthOptions}
          value={currentOption || null}
          onChange={opt => opt && onChange(opt.value)}
          placeholder={t('select-month')}
          isClearable={false}
        />
      </div>

      <button
        type="button"
        onClick={handleNextMonth}
        disabled={!canGoNext}
        className={chipBase}
        style={{ color: getColour('normalText') }}
        onMouseEnter={e => {
          if (canGoNext)
            e.currentTarget.style.backgroundColor = getBrandTint(0.06);
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = '';
        }}
        title={t('next-month')}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
